import { useEffect, useState } from 'react'
import { Loader2, Sparkles, X } from 'lucide-react'

import { Button } from 'components/ui/button'
import { Checkbox } from 'components/ui/checkbox'
import { Input } from 'components/ui/input'
import { Sheet } from 'components/ui/sheet'
import { cn } from 'lib/utils'
import { suggestScores, type ScoreSuggestion, type ScoreSuggestions } from 'services/aiService'

/** One score to write: what the user left ticked, with their edits. */
export interface ScoreToApply {
	criterionId: string
	value: number
	justification: string
}

interface Row {
	suggestion: ScoreSuggestion
	checked: boolean
	value: string
}

const CONFIDENCE_STYLE: Record<string, string> = {
	high: 'bg-emerald-50 text-emerald-700',
	medium: 'bg-amber-50 text-amber-700',
	low: 'bg-gray-100 text-gray-600',
}

/** "Based on 6 site details · 3 demographic areas · 12 nearby retailers · …" */
export const basedOnText = (basedOn: ScoreSuggestions['basedOn']): string =>
	[
		`${basedOn.siteFacts} site detail${basedOn.siteFacts === 1 ? '' : 's'}`,
		basedOn.demographics ? `${basedOn.demographics} demographic area${basedOn.demographics === 1 ? '' : 's'}` : 'no demographics',
		basedOn.retailers ? `${basedOn.retailers} saved nearby retailer${basedOn.retailers === 1 ? '' : 's'}` : 'no saved nearby retailers',
		basedOn.prosAndCons ? `${basedOn.prosAndCons} pros and cons` : null,
		basedOn.risks ? `${basedOn.risks} risk${basedOn.risks === 1 ? '' : 's'}` : null,
		basedOn.financials === 'included' ? 'financials' : basedOn.financials === 'not on this plan' ? 'financials not included on your plan' : null,
	]
		.filter(Boolean)
		.join(' · ')

const isScore = (raw: string) => {
	const n = Number(raw)
	return raw.trim() !== '' && Number.isFinite(n) && n >= 1 && n <= 10
}

/**
 * The justification saved with an accepted suggestion: says it came from
 * Ask MDT and that the user accepted it, and what it rested on, so anyone
 * reading the score later knows where it came from.
 */
export const justificationFor = (suggestion: ScoreSuggestion, value: number): string => {
	const edited = suggestion.score !== null && value !== suggestion.score ? ` (edited from ${suggestion.score})` : ''
	const evidence = suggestion.evidence.map(fact => fact.label).join('; ')
	return `AI suggestion accepted${edited}: ${suggestion.rationale}${evidence ? ` Evidence: ${evidence}.` : ''}`
}

const messageOf = (error: any): string => error?.response?.data?.message || "Ask MDT couldn't suggest scores just now. Try again in a moment."

/**
 * "Suggest scores" for one site (MYD-641). Asks once on open, then shows each
 * criterion's suggestion beside the site's current score, with the rationale
 * and the facts it cites. The user unticks or edits rows, then applies.
 *
 * Nothing is written until Apply, and then only the ticked rows, through
 * `onApply`: the ordinary score endpoint, as the user's own score.
 */
export function ScoreSuggestionsSheet({
	open,
	onClose,
	evaluationId,
	site,
	onApply,
	suggest = suggestScores,
}: {
	open: boolean
	onClose: () => void
	evaluationId: string
	site: { id: string; name: string }
	onApply: (siteId: string, scores: ScoreToApply[]) => Promise<void>
	suggest?: typeof suggestScores
}) {
	const [result, setResult] = useState<ScoreSuggestions | null>(null)
	const [rows, setRows] = useState<Row[]>([])
	const [loading, setLoading] = useState(false)
	const [applying, setApplying] = useState(false)
	const [error, setError] = useState<string | null>(null)

	useEffect(() => {
		if (!open) return
		let cancelled = false
		setLoading(true)
		setError(null)
		setResult(null)
		suggest(evaluationId, site.id)
			.then(answer => {
				if (cancelled) return
				setResult(answer)
				setRows(answer.suggestions.map(suggestion => ({ suggestion, checked: suggestion.score !== null, value: suggestion.score === null ? '' : String(suggestion.score) })))
			})
			.catch(failure => !cancelled && setError(messageOf(failure)))
			.finally(() => !cancelled && setLoading(false))
		return () => {
			cancelled = true
		}
	}, [open, evaluationId, site.id, suggest])

	const update = (index: number, patch: Partial<Row>) => setRows(current => current.map((row, i) => (i === index ? { ...row, ...patch } : row)))

	const chosen = rows.filter(row => row.checked && row.suggestion.score !== null)
	const invalid = chosen.some(row => !isScore(row.value))

	const apply = async () => {
		setApplying(true)
		setError(null)
		try {
			await onApply(
				site.id,
				chosen.map(row => ({ criterionId: row.suggestion.criterionId, value: Number(row.value), justification: justificationFor(row.suggestion, Number(row.value)) })),
			)
			onClose()
		} catch (failure: any) {
			setError(typeof failure === 'string' ? failure : failure?.message || 'Some scores could not be saved. Check the matrix and try again.')
		} finally {
			setApplying(false)
		}
	}

	return (
		<Sheet open={open} onClose={onClose} side="right" className="w-full sm:w-[520px]">
			<div className="flex items-start justify-between gap-3 border-b border-gray-200 px-5 py-4">
				<div className="min-w-0">
					<h2 className="flex items-center gap-2 text-base font-semibold text-gray-900">
						<Sparkles className="h-4 w-4 text-primary" aria-hidden="true" />
						Suggested scores
					</h2>
					<p className="truncate text-sm text-gray-600">{site.name}</p>
				</div>
				<button type="button" onClick={onClose} aria-label="Close suggested scores" className="rounded p-1 text-gray-500 hover:bg-gray-100">
					<X className="h-4 w-4" />
				</button>
			</div>

			<div className="flex-1 space-y-3 px-5 py-4">
				{loading && (
					<p className="flex items-center gap-2 text-sm text-gray-600" role="status">
						<Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Reading this site’s details, demographics and nearby retailers…
					</p>
				)}
				{error && (
					<p role="alert" className="text-sm text-destructive">
						{error}
					</p>
				)}
				{result && (
					<>
						<p className="text-xs text-gray-500">AI suggestions: nothing is saved until you apply. Based on {basedOnText(result.basedOn)}.</p>
						<ul className="space-y-2">
							{rows.map((row, index) => {
								const { suggestion } = row
								const none = suggestion.score === null
								return (
									<li key={suggestion.criterionId} className={cn('rounded-lg border border-gray-200 p-3', none && 'bg-gray-50')}>
										<div className="flex items-start gap-3">
											<Checkbox
												checked={row.checked}
												disabled={none}
												onCheckedChange={checked => update(index, { checked })}
												aria-label={`Use the suggestion for ${suggestion.criterion}`}
												className="mt-0.5"
											/>
											<div className="min-w-0 flex-1">
												<div className="flex items-center justify-between gap-2">
													<p className="text-sm font-medium text-gray-900">
														{suggestion.criterion} <span className="text-xs font-normal text-gray-400">{suggestion.weight}%</span>
													</p>
													{!none && (
														<Input
															type="number"
															min={1}
															max={10}
															step={0.5}
															value={row.value}
															aria-label={`Score for ${suggestion.criterion}`}
															onChange={event => update(index, { value: event.target.value, checked: true })}
															className="h-8 w-16 shrink-0 px-1 text-center text-sm"
														/>
													)}
												</div>
												<p className="mt-0.5 text-xs text-gray-500">
													Now {suggestion.currentScore ?? '—'}
													{suggestion.confidence && <span className={cn('ml-2 rounded px-1.5 py-0.5 text-[10px] font-medium', CONFIDENCE_STYLE[suggestion.confidence])}>{suggestion.confidence} confidence</span>}
												</p>
												<p className={cn('mt-1 text-sm', none ? 'text-gray-500' : 'text-gray-700')}>{suggestion.rationale}</p>
												{suggestion.evidence.length > 0 && (
													<ul className="mt-1.5 space-y-0.5 border-l-2 border-gray-200 pl-2">
														{suggestion.evidence.map(fact => (
															<li key={fact.id} className="text-xs text-gray-500">
																<span className="font-medium text-gray-600">{fact.label}:</span> {fact.text}
															</li>
														))}
													</ul>
												)}
											</div>
										</div>
									</li>
								)
							})}
						</ul>
					</>
				)}
			</div>

			{result && (
				<div className="sticky bottom-0 flex items-center justify-between gap-3 border-t border-gray-200 bg-white px-5 py-3">
					<p className="text-xs text-gray-500">{invalid ? 'Scores run from 1 to 10.' : `${chosen.length} of ${rows.length} ticked`}</p>
					<Button type="button" variant="primary" onClick={apply} disabled={applying || chosen.length === 0 || invalid}>
						{applying ? <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" /> : null}
						{applying ? 'Applying…' : `Apply ${chosen.length} score${chosen.length === 1 ? '' : 's'}`}
					</Button>
				</div>
			)}
		</Sheet>
	)
}

export default ScoreSuggestionsSheet
